/**
 * @file chat-errors.ts
 * @description Коды ошибок сервера переписки → текст для человека
 * (cabinet-api-contract.md §12). Реестр, а не каскад условий: новый код —
 * новая строка. Неизвестный код — запасной текст вызывающего места, а не
 * технический `message` сервера.
 */

import { ApiError } from "../../lib/api-client";
import type { ConversationStateDetails } from "../../lib/api-chat";
import { CHAT_TEXT } from "./chat-texts";

const CHAT_ERROR_TEXT: Readonly<Record<string, string>> = {
  chat_forbidden: CHAT_TEXT.noAccess,
  rbac_shop_only: CHAT_TEXT.noAccess,
  chat_impersonation_read_only: CHAT_TEXT.stripReadOnly,
  conversation_not_found: "Диалог не найден или больше вам не назначен",
  chat_conversation_archived: "Диалог в архиве — сначала верните его из архива",
  marketing_chat_consent_required:
    "Клиент не давал согласия на рекламу — отправьте сообщение без пометки «Реклама»",
  chat_dispatch_forbidden: "Это действие доступно владельцу и оператору заказов",
  chat_transfer_target_invalid: "Этому сотруднику нельзя передать диалог: он не работает или у него нет права переписки",
  chat_transfer_same_assignee: "Диалог уже ведёт этот сотрудник",
  chat_transfer_to_self: "Чтобы вести диалог самому, нажмите «Взять себе»",
  chat_return_not_applicable: "Вернуть оператору может только ответственный логист",
  chat_customer_required: "Не выбран клиент",
  customer_not_found: "Клиент не найден",
  chat_context_not_found: "Заказ не относится к этому клиенту или магазину",
  chat_search_too_long: "Слишком длинный запрос — до 100 знаков",
  network_error: "Нет связи с сервером. Проверьте подключение и попробуйте ещё раз",
};

/** Текст ошибки для всплывающего сообщения. */
export function chatErrorText(error: unknown, fallback: string): string {
  if (error instanceof ApiError) return CHAT_ERROR_TEXT[error.code] ?? fallback;
  return fallback;
}

/** Ошибка — гонка действий (409 `chat_conversation_state_changed`)? */
export function raceDetails(error: unknown): ConversationStateDetails | null {
  if (!(error instanceof ApiError) || error.code !== "chat_conversation_state_changed") return null;
  const d = error.details as Partial<ConversationStateDetails> | null | undefined;
  return { assignedUser: d?.assignedUser ?? null, archived: d?.archived === true };
}

/** «Пока вы выбирали, диалог передали …» по деталям гонки. */
export function raceText(details: ConversationStateDetails): string {
  return CHAT_TEXT.raceChanged(details.assignedUser?.name ?? null, details.archived);
}

/** Ошибка из-за того, что диалог больше не виден (логисту его сняли). */
export function isLostConversation(error: unknown): boolean {
  return error instanceof ApiError && error.code === "conversation_not_found";
}
